import { Outlet, useParams } from "react-router";
import { useGetProcessQuery } from "@/services/backend/processes/process/service";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  NavigationGroup,
  ProcessPage,
  ProcessPageMain,
  ProcessPageMainContent,
  ProcessPageMainHeader,
  ProcessPageSidebar,
  ProcessPageSidebarFooter,
  ProcessPageSidebarNavigation,
  ProcessSidebarTrigger,
} from "@/shared/service-manager/default-ui/process-page/process-page";
import { BreadcrumbsToArtefact } from "@/shared/service-manager/default-ui/process-page/breadcrumbs-to-artefact";
import { Separator } from "@/shared/components/ui/separator";
import {
  Binoculars,
  ClipboardList,
  FlaskRound,
  Layout,
  PackageOpen,
  Phone,
  Shapes,
} from "lucide-react";
import {
  InspectionRule,
  useGetInspectionRulesQuery,
} from "@/services/backend/rules/service";
import t from "@/lang/lang";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
} from "@/shared/components/ui/sidebar";
import { CloseProcessSidebarMenu } from "./_components/close-process-sidebar-menu";
import { NextProcessDropdown } from "./_components/next-process-dropdown";

const sidebarNavigation: NavigationGroup[] = [
  {
    label: "Allgemein",
    items: [
      {
        label: "Übersicht",
        icon: Layout,
        to: "overview",
      },
      {
        label: "Ansprechpartner",
        icon: Phone,
        to: "contact",
      },
    ],
  },
  {
    label: "Meilensteine",
    items: [
      {
        label: "Lieferung",
        icon: PackageOpen,
        to: "delivery",
      },
      {
        label: "Dokumentation",
        icon: FlaskRound,
        to: "documentation",
      },
      {
        label: "Zählung",
        icon: Binoculars,
        to: "counting",
      },
      {
        label: "Differenzierung",
        icon: Shapes,
        to: "differentiation",
      },
      {
        label: "Prüfberichte",
        icon: ClipboardList,
        to: "inspection",
      },
    ],
  },
];

function createSidebarNavigation(rule: InspectionRule): NavigationGroup[] {
  const filteredNavigation = sidebarNavigation.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (item.to === "differentiation") return rule.hasDifferentiation;
      return true;
    }),
  }));

  return filteredNavigation;
}

export default function ProcessRoute() {
  const { processId } = useParams();
  const { data: rule } = useGetInspectionRulesQuery({ processId: processId! });
  const {
    data: process,
    isLoading,
    error,
  } = useGetProcessQuery({
    id: processId ?? "",
  });

  if (isLoading) {
    return null;
  }

  if (!rule) {
    return null;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <ProcessPage>
      <ProcessPageSidebar className="pt-16">
        <ProcessPageSidebarNavigation groups={createSidebarNavigation(rule)} />
        <ProcessPageSidebarFooter>
          <SidebarGroup>
            <SidebarGroupLabel>{t("Vorgang")}</SidebarGroupLabel>
            <SidebarGroupContent>
              <CloseProcessSidebarMenu processId={process!.id} />
            </SidebarGroupContent>
          </SidebarGroup>
        </ProcessPageSidebarFooter>
      </ProcessPageSidebar>
      <ProcessPageMain>
        <ProcessPageMainHeader className="justify-between">
          <div className="flex items-center gap-2">
            <ProcessSidebarTrigger />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <BreadcrumbsToArtefact process={process!} />
          </div>
          <NextProcessDropdown process={process!} />
        </ProcessPageMainHeader>
        <ProcessPageMainContent>
          <Outlet />
        </ProcessPageMainContent>
      </ProcessPageMain>
    </ProcessPage>
  );
}
